import { DatePicker } from '@/design-system/components/date-picker';
import { Clock3, Layers3, Receipt, CheckCircle2, Wallet, FileClock } from 'lucide-react';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatMoney, CONTRACT_MODEL_LABELS, type ClientFinanceSummaryDto } from '@cw/shared';
import { api } from '@/lib/api';
import { useCan } from '@/lib/session';
import { handleFormError } from '@/lib/forms';
import { useInvoices } from '@/lib/finance';
import { Select } from '@/design-system/components/select';
import { Skeleton } from '@/design-system/components/skeleton';
import { EmptyState, ErrorState } from '@/design-system/components/states';
import { InvoiceList, InvoiceSheet } from './invoices-tab';

export function ClientFinance({ clientId }: { clientId: string }) {
  const [period, setPeriod] = useState({ from: '', to: '' });
  const [open, setOpen] = useState<string | null>(null);
  const qc = useQueryClient();
  const canWrite = useCan('finance:write');
  const params = new URLSearchParams();
  if (period.from) params.set('from', period.from);
  if (period.to) params.set('to', period.to);
  const summary = useQuery({
    queryKey: ['finance', 'client-summary', clientId, params.toString()],
    queryFn: () => api.get<ClientFinanceSummaryDto>(`/finance/clients/${clientId}/summary?${params}`),
  });
  const invoices = useInvoices({ clientId });
  const assign = useMutation({
    mutationFn: ({ taskId, contractId }: { taskId: string; contractId: string }) =>
      api.patch(`/tasks/${taskId}`, { contractId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['finance'] }),
    onError: (e) => handleFormError(e),
  });
  const data = summary.data;
  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[28px] font-semibold tracking-tight">Financeiro</h1>
          <p className="mt-1 text-sm text-fg-muted">Trabalhos, contrato e pagamentos em um só lugar.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <label className="text-xs text-fg-muted">
            De
            <DatePicker
              value={period.from || data?.from || ''}
              onValueChange={(v) => setPeriod((p) => ({ ...p, from: v }))}
            />
          </label>
          <label className="text-xs text-fg-muted">
            Até
            <DatePicker
              value={period.to || data?.to || ''}
              onValueChange={(v) => setPeriod((p) => ({ ...p, to: v }))}
            />
          </label>
        </div>
      </header>
      {summary.isPending ? (
        <Skeleton className="h-64" />
      ) : summary.isError ? (
        <ErrorState error={summary.error} onRetry={() => summary.refetch()} />
      ) : (
        data && (
          <>
            <div className="grid gap-3 md:grid-cols-2">
              {data.contracts.map((c) => (
                <article key={c.id} className="rounded-panel border border-line bg-raised p-5">
                  <p className="text-xs text-fg-muted">{CONTRACT_MODEL_LABELS[c.model]}</p>
                  <h2 className="mt-1 font-semibold">{c.title}</h2>
                  <p className="mt-3 text-2xl font-semibold tabular-nums">
                    {c.model === 'volume' ? 'Por produção' : formatMoney(c.amountCents)}
                  </p>
                  <p className="mt-1 text-xs text-fg-muted">
                    {
                      { monthly: 'Mensal', quarterly: 'Trimestral', yearly: 'Anual', none: 'Valor único' }[
                        c.recurrence
                      ]
                    }{' '}
                    · {c.startsOn.split('-').reverse().join('/')}
                    {c.endsOn ? ` a ${c.endsOn.split('-').reverse().join('/')}` : ''}
                  </p>
                </article>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3 xl:grid-cols-3">
              {(data.contracts.some((c) => c.model === 'volume')
                ? [
                    ['Em produção', data.productionCents],
                    ['Acumulado do período', data.accumulatedCents],
                    ['A faturar', data.toInvoiceCents],
                    ['Faturado', data.invoicedCents],
                    ['Pago', data.paidCents],
                    ['Em aberto', data.openCents],
                  ]
                : [
                    ['Faturado', data.invoicedCents],
                    ['Pago', data.paidCents],
                    ['Em aberto', data.openCents],
                  ]
              ).map(([label, value]) => (
                <div
                  key={label}
                  className="cw-rise rounded-panel border border-line bg-stage p-4 transition-colors hover:bg-hover"
                >
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <p className="text-xs text-fg-muted">{label}</p>
                    <span className="rounded-control bg-signal/10 p-2 text-signal">
                      {label === 'Em produção' ? (
                        <Clock3 className="size-4" />
                      ) : label === 'Acumulado do período' ? (
                        <Layers3 className="size-4" />
                      ) : label === 'Pago' ? (
                        <CheckCircle2 className="size-4" />
                      ) : label === 'Em aberto' ? (
                        <Wallet className="size-4" />
                      ) : label === 'A faturar' ? (
                        <FileClock className="size-4" />
                      ) : (
                        <Receipt className="size-4" />
                      )}
                    </span>
                  </div>
                  <p className="mt-2 text-xl font-semibold tabular-nums">{formatMoney(Number(value))}</p>
                </div>
              ))}
            </div>
            {data.pendingCount > 0 && (
              <p role="status" className="rounded-control border border-warning/30 bg-warning/10 p-3 text-sm">
                {data.pendingCount}{' '}
                {data.pendingCount === 1
                  ? 'item com valor ou contrato a definir'
                  : 'itens com valor ou contrato a definir'}
                . Os totais consideram somente valores definidos.
              </p>
            )}
            <section>
              <h2 className="mb-3 text-lg font-semibold">Trabalhos do período</h2>
              <div className="overflow-x-auto rounded-panel border border-line">
                <table className="w-full min-w-[640px] text-left text-sm">
                  <thead className="bg-raised text-xs text-fg-muted">
                    <tr>
                      {['Trabalho / material', 'Quantidade', 'Valor unitário', 'Total', 'Situação'].map(
                        (h) => (
                          <th key={h} className="px-4 py-3 font-medium">
                            {h}
                          </th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {data.items.map((i) => (
                      <tr key={i.id} className="transition-colors hover:bg-hover">
                        <td className="px-4 py-3">
                          <p className="font-medium">{i.title}</p>
                          <p className="text-xs text-fg-muted">
                            {i.project} · {i.service}
                          </p>
                          {canWrite && i.pending === 'contract' && (
                            <Select
                              aria-label={`Contrato de ${i.title}`}
                              value=""
                              placeholder="Vincular contrato"
                              onValueChange={(contractId) => assign.mutate({ taskId: i.taskId, contractId })}
                              options={data.contracts.map((c) => ({ value: c.id, label: c.title }))}
                            />
                          )}
                        </td>
                        <td className="px-4 py-3">{i.quantity || 'A definir'}</td>
                        <td className="px-4 py-3">
                          {i.model === 'fixed'
                            ? 'Incluso'
                            : i.unitPriceCents === null
                              ? 'A definir'
                              : formatMoney(i.unitPriceCents)}
                        </td>
                        <td className="px-4 py-3 tabular-nums">
                          {i.model === 'fixed'
                            ? 'Incluso'
                            : i.totalCents === null
                              ? 'A definir'
                              : formatMoney(i.totalCents)}
                        </td>
                        <td className="px-4 py-3">
                          <span className="rounded-full bg-press px-2 py-1 text-xs">
                            {i.billed ? 'Faturado' : i.completed ? 'Concluído' : 'Em produção'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!data.items.length && (
                  <p className="p-8 text-center text-sm text-fg-muted">Nenhum trabalho neste período.</p>
                )}
              </div>
              <p className="mt-2 text-xs text-fg-muted">
                Em produção é uma previsão. O acumulado considera os trabalhos aprovados ou concluídos.
                Valores fixos não geram extras automaticamente.
              </p>
            </section>
          </>
        )
      )}
      <section>
        <h2 className="mb-3 text-lg font-semibold">Faturas e pagamentos</h2>
        {invoices.isPending ? (
          <Skeleton className="h-24" />
        ) : invoices.isError ? (
          <ErrorState error={invoices.error} onRetry={() => invoices.refetch()} />
        ) : !invoices.data.length ? (
          <EmptyState
            icon={<Receipt />}
            title="Nenhuma fatura ainda"
            description="As faturas e os pagamentos aparecerão aqui quando forem registrados."
          />
        ) : (
          <InvoiceList invoices={invoices.data} onOpen={setOpen} />
        )}
      </section>
      <InvoiceSheet id={open} onClose={() => setOpen(null)} readOnly />
    </div>
  );
}
export function TaskContractSelect({
  clientId,
  value,
  onChange,
}: {
  clientId: string;
  value?: string | null;
  onChange: (id: string) => void;
}) {
  const contracts = useQuery({
    queryKey: ['tasks', 'contract-options', clientId],
    queryFn: () => api.get<{ id: string; title: string }[]>(`/tasks/contract-options?clientId=${clientId}`),
  });
  return (
    <Select
      aria-label="Contrato do trabalho"
      value={value ?? ''}
      placeholder="Contrato do trabalho"
      options={(contracts.data ?? []).map((c) => ({ value: c.id, label: c.title }))}
      onValueChange={onChange}
    />
  );
}
